import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ReactDOM from 'react-dom';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import Navbar from './Navbar/Navbar';
import LoginPage from './Login/LoginPage';
import RegistrationPage from './Login/RegistrationPage';
import HomePage from './Homepage/HomePage';
import ShipDashboard from './Ship/ShipDashboard';
import VesselProfile from './Vessel/VesselProfile';
import ShipLocation from './Vessel/ShipLocation';
import EnvironmentalImpactPage from './EI/EI';
import BiofoulingMonitoringPage from './BFM/Bfm';
import Performance from './Performance/Performance';
import Fuel from './Map/Fuel/Fuel';
import Port from './Map/Port';
import BotsMap from './Map/Bots';
import Globe from './Map/Globe';
import Visualization from './Fire';
import Game_Login from './Game_Login';
import GeminiChat from './GeminiChat';
import MapPage from './Map/MapPage';
import { LoginInfoProvider } from './Login/LoginInfo';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';

// Firebase initialization
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: 'dashboard-c2d5d',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

if (!firebase.apps.length) firebase.initializeApp(firebaseConfig);
const db = firebase.firestore();

// Function to fetch and store user city and country based on IP
const fetchAndStoreUserLocation = async (userId) => {
  try {
    const response = await fetch(`https://ipapi.co/json/`);
 // Replace with API that requires a key
    const data = await response.json();
    if (data) {
      const location = {
        city: data.city,
        country: data.country_name,
        timestamp: firebase.firestore.FieldValue.serverTimestamp(),
      };
      console.log('User location:', location);
      await db.collection('userActivity').doc(userId).set(
        { location },
        { merge: true }
      );
    }
  } catch (error) {
    console.error('Error fetching user location:', error);
  }
};



// ProtectedRoute component
const ProtectedRoute = ({ children, isLoggedIn }) => isLoggedIn ? children : <Navigate replace to="/login" />;

// Navbar visibility handler
const ShowNavbar = ({ isLoggedIn, handleLogout }) => {
  const location = useLocation();
  const hideNavbarOnPaths = ['/login', '/register', '/globe', '/fuel'];
  return !hideNavbarOnPaths.includes(location.pathname) && <Navbar isLoggedIn={isLoggedIn} onLogout={handleLogout} />;
};

// Gemini chat visibility handler
const ShowGeminiChat = () => {
  const location = useLocation();
  const hideChatOnPaths = ['/login', '/register'];
  return !hideChatOnPaths.includes(location.pathname) && <GeminiChat />;
};

// Track page time for analytics
const TrackPageTime = () => {
  const location = useLocation();
  const [startTime, setStartTime] = useState(null);

  useEffect(() => {
    const pageStartTime = new Date();
    setStartTime(pageStartTime);

    return () => {
      const pageEndTime = new Date();
      const timeSpent = (pageEndTime - pageStartTime) / 1000;
      const user = firebase.auth().currentUser;
      if (user) {
        const userRef = db.collection('userActivity').doc(user.uid);
        userRef.collection('pageVisits').add({
          path: location.pathname,
          timeSpent,
          timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        });
      }
    };
  }, [location]);

  return null;
};

const App = () => {
  const [isLoggedIn, setLoggedIn] = useState(false);
  const [isFirePageLoggedIn, setIsFirePageLoggedIn] = useState(false);

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
      if (user) {
        const email = user.email;
        if (email.endsWith('@gitcoatings.com')) {
          setLoggedIn(true);
          const userRef = db.collection('userActivity').doc(user.uid);
          await userRef.set(
            { email: user.email, loginTime: firebase.firestore.FieldValue.serverTimestamp() },
            { merge: true }
          );
          // Fetch and store user location
          fetchAndStoreUserLocation(user.uid);
        } else {
          alert('Only users with @gitcoatings.com domain can log in.');
          handleLogout();
        }
      } else {
        setLoggedIn(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      const user = firebase.auth().currentUser;
      if (user) {
        const userRef = db.collection('userActivity').doc(user.uid);
        const doc = await userRef.get();
        if (doc.exists) {
          const loginTime = doc.data().loginTime?.toDate();
          const logoutTime = new Date();
          const sessionTimeInMinutes = (logoutTime - loginTime) / 1000 / 60; // Convert to minutes
          const totalTimeSpent = doc.data().totalTimeSpent || 0;
          await userRef.set(
            {
              logoutTime: firebase.firestore.FieldValue.serverTimestamp(),
              timeSpent: sessionTimeInMinutes, // Store time in minutes
              totalTimeSpent: totalTimeSpent + sessionTimeInMinutes,
            },
            { merge: true }
          );
        }
      }
      await firebase.auth().signOut();
      setLoggedIn(false);
      setIsFirePageLoggedIn(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    let logoutTimer;
    if (isLoggedIn) {
      logoutTimer = setTimeout(() => {
        alert('Your session has expired due to inactivity.');
        handleLogout();
      }, 3600000); // Logs out after 1 hour of inactivity
    }
    return () => clearTimeout(logoutTimer); // Cleanup on component unmount or re-login
  }, [isLoggedIn]);

  return (
    <LoginInfoProvider>
      <Analytics />
      <SpeedInsights />
      <Router>
        <div>
          <TrackPageTime />
          <ShowNavbar isLoggedIn={isLoggedIn} handleLogout={handleLogout} />
          <Routes>
            <Route path="/" element={isLoggedIn ? <Navigate replace to="/home" /> : <Navigate replace to="/login" />} />
            <Route path="/login" element={isLoggedIn ? <Navigate replace to="/home" /> : <LoginPage />} />
            <Route path="/register" element={<RegistrationPage />} />
            {[
              { path: '/home', component: <HomePage onLogout={handleLogout} /> },
              { path: '/ships', component: <ShipDashboard /> },
              { path: '/vessel-profile', component: <VesselProfile /> },
              { path: '/ship-location', component: <ShipLocation /> },
              { path: '/biofouling-smart-monitoring', component: <BiofoulingMonitoringPage /> },
              { path: '/environmental-impact', component: <EnvironmentalImpactPage /> },
              { path: '/case-studies', component: <Performance /> },
              { path: '/fuel', component: <Fuel /> },
              { path: '/port', component: <Port /> },
              { path: '/bot', component: <BotsMap /> },
              { path: '/globe', component: <Globe /> },
              { path: '/mapdata', component: <MapPage /> },
            ].map(({ path, component }) => (
              <Route key={path} path={path} element={<ProtectedRoute isLoggedIn={isLoggedIn}>{component}</ProtectedRoute>} />
            ))}
            <Route path="/fire-login" element={isFirePageLoggedIn ? <Navigate replace to="/fire" /> : <Game_Login />} />
            <Route path="/fire" element={isFirePageLoggedIn ? <Visualization /> : <Navigate replace to="/fire-login" />} />
          </Routes>
          <ShowGeminiChat />
        </div>
      </Router>
    </LoginInfoProvider>
  );
};

const rootElement = document.getElementById('root');
ReactDOM.render(<App />, rootElement);

export default App;
